import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import {
  TrackingSessionResponse, startTracking, stopTracking, sendPosition,
  getMySessions, updateTarget, StartTrackingRequest, getActiveSessionForPlanning,
  StartDakoTrackingRequest, startDakoTracking, stopDakoTracking,
} from '../services/trackingApi';
import { matchdayPlanningApi } from '../services/matchdayPlanningApi';
import { PlanningSegment } from '../types/matchdayPlanning';
import { useAuth } from './AuthContext';

interface TrackingContextType {
  activeSession: TrackingSessionResponse | null;
  /** True if the active session was started by the current user on this device */
  isOwnSession: boolean;
  activeSegments: PlanningSegment[];
  isSending: boolean;
  lastPosition: { lat: number; lon: number } | null;
  lastUpdateTime: Date | null;
  posCount: number;
  targetUpdating: boolean;
  error: string | null;
  trackingInterval: number;
  keepScreenOn: boolean;
  handleStartSession: (req: StartTrackingRequest, interval?: number, screenOn?: boolean) => Promise<void>;
  /** Start a DAKO (vehicle telematics) session. Positions are polled server-side, so no browser GPS is used. */
  handleStartDakoSession: (req: StartDakoTrackingRequest) => Promise<void>;
  handleStopSession: () => Promise<void>;
  handleChangeTarget: (segmentId: number | null) => Promise<void>;
  clearError: () => void;
  refreshSessions: () => Promise<void>;
}

const TrackingContext = createContext<TrackingContextType | undefined>(undefined);

export const useTracking = (): TrackingContextType => {
  const ctx = useContext(TrackingContext);
  if (!ctx) throw new Error('useTracking must be used within TrackingProvider');
  return ctx;
};

export const TrackingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [activeSession, setActiveSession] = useState<TrackingSessionResponse | null>(null);
  const [isOwnSession, setIsOwnSession] = useState(true);
  const [activeSegments, setActiveSegments] = useState<PlanningSegment[]>([]);
  const [targetUpdating, setTargetUpdating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [lastPosition, setLastPosition] = useState<{ lat: number; lon: number } | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [posCount, setPosCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [trackingInterval, setTrackingInterval] = useState(2);
  const [keepScreenOn, setKeepScreenOn] = useState(true);
  const watchIdRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastCoordsRef = useRef<GeolocationCoordinates | null>(null);
  const isSendingRef = useRef(false);
  const wakeLockRef = useRef<any>(null);
  const failedSendsRef = useRef(0);
  // Stop tracking after this many consecutive failed position sends so the user
  // is notified instead of the session silently looping without updates.
  const MAX_FAILED_SENDS = 5;

  // Wake Lock management
  const requestWakeLock = useCallback(async () => {
    if (!keepScreenOn || !('wakeLock' in navigator)) return;
    try {
      wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
      wakeLockRef.current.addEventListener('release', () => { wakeLockRef.current = null; });
    } catch (err) {
      console.warn('Wake Lock request failed:', err);
    }
  }, [keepScreenOn]);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  // Re-acquire wake lock on visibility change (tab switch, screen unlock)
  useEffect(() => {
    if (!activeSession || !keepScreenOn) return;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && activeSession) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [activeSession, keepScreenOn, requestWakeLock]);

  const refreshSessions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await getMySessions();
      const myActive = data.find(s => s.status === 'Active');
      if (myActive) {
        setActiveSession(prev => (prev && prev.id === myActive.id) ? prev : myActive);
        setIsOwnSession(true);
      } else {
        // No own active session – check if someone else is tracking for a planning
        // We don't have a specific planning to check here, so clear own session
        setActiveSession(null);
        setIsOwnSession(true);
      }
    } catch (err: any) {
      console.error('TrackingContext: Failed to load sessions', err);
    }
  }, [isAuthenticated]);

  useEffect(() => { refreshSessions(); }, [refreshSessions]);

  useEffect(() => {
    if (activeSession?.planningId) {
      matchdayPlanningApi.getSegments(activeSession.planningId)
        .then(data => setActiveSegments([...data].sort((a, b) => a.sequenceOrder - b.sequenceOrder)))
        .catch(err => console.error('Failed to load segments:', err));
    } else { setActiveSegments([]); }
  }, [activeSession?.planningId]);

  useEffect(() => {
    // DAKO sessions get their positions from the vehicle telematics unit via
    // server-side polling — never engage the browser's Geolocation API for them.
    if (activeSession && isOwnSession && activeSession.source !== 'Dako' && watchIdRef.current === null) {
      startGeoWatch(activeSession);
    }
  }, [activeSession?.id, isOwnSession, activeSession?.source]);

  const startGeoWatch = (session: TrackingSessionResponse, intervalOverride?: number) => {
    if (!navigator.geolocation) { setError('Geolocation nicht unterstuetzt.'); return; }
    stopGeoWatch();
    failedSendsRef.current = 0;
    const effectiveInterval = intervalOverride ?? trackingInterval;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => { lastCoordsRef.current = pos.coords; },
      (err) => console.error('Geo error:', err),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    intervalRef.current = setInterval(async () => {
      const coords = lastCoordsRef.current;
      if (!coords || isSendingRef.current) return;
      isSendingRef.current = true;
      setIsSending(true);
      try {
        await sendPosition(session.id, {
          latitude: coords.latitude, longitude: coords.longitude,
          speed: coords.speed, heading: coords.heading, accuracy: coords.accuracy,
        });
        setLastPosition({ lat: coords.latitude, lon: coords.longitude });
        setLastUpdateTime(new Date());
        setPosCount(c => c + 1);
        failedSendsRef.current = 0;
      } catch (err: any) {
        failedSendsRef.current += 1;
        console.error(`Position send error (${failedSendsRef.current}/${MAX_FAILED_SENDS}):`, err);
        const status = err?.response?.status;
        // A persistent 401 means the token could not be refreshed (session truly
        // expired) — stop immediately. Otherwise stop after repeated failures.
        if (status === 401 || failedSendsRef.current >= MAX_FAILED_SENDS) {
          stopGeoWatch();
          releaseWakeLock();
          setError(
            status === 401
              ? 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an und starte das Tracking neu.'
              : 'Position konnte mehrfach nicht gesendet werden. Tracking wurde gestoppt.'
          );
        }
      }
      finally { isSendingRef.current = false; setIsSending(false); }
    }, effectiveInterval * 1000);
  };

  const stopGeoWatch = () => {
    if (watchIdRef.current !== null) { navigator.geolocation.clearWatch(watchIdRef.current); watchIdRef.current = null; }
    if (intervalRef.current !== null) { clearInterval(intervalRef.current); intervalRef.current = null; }
  };

  const handleStartSession = async (req: StartTrackingRequest, interval?: number, screenOn?: boolean) => {
    try {
      if (interval !== undefined) setTrackingInterval(interval);
      if (screenOn !== undefined) setKeepScreenOn(screenOn);
      const effectiveInterval = interval ?? trackingInterval;
      const effectiveScreenOn = screenOn ?? keepScreenOn;
      const session = await startTracking(req);
      setActiveSession(session);
      // Use effective values for geo watch (state may not have updated yet)
      startGeoWatch(session, effectiveInterval);
      if (effectiveScreenOn) requestWakeLock();
    } catch (err: any) { setError(err.message); throw err; }
  };

  const handleStartDakoSession = async (req: StartDakoTrackingRequest) => {
    try {
      // No browser GPS / wake lock for DAKO — the backend polls the vehicle and
      // pushes positions to live viewers via SignalR.
      const session = await startDakoTracking(req);
      setActiveSession(session);
    } catch (err: any) { setError(err.message); throw err; }
  };

  const handleStopSession = async () => {
    if (!activeSession) return;
    try {
      if (activeSession.source === 'Dako') {
        await stopDakoTracking(activeSession.id);
      } else {
        await stopTracking(activeSession.id);
      }
      stopGeoWatch();
      releaseWakeLock();
      setActiveSession(null);
      setLastPosition(null);
      setLastUpdateTime(null);
      setPosCount(0);
    } catch (err: any) { setError(err.message); }
  };

  const handleChangeTarget = async (segmentId: number | null) => {
    if (!activeSession || targetUpdating) return;
    setTargetUpdating(true);
    try {
      const updated = await updateTarget(activeSession.id, segmentId);
      setActiveSession(prev => prev ? { ...prev, targetSegmentId: updated.targetSegmentId } : prev);
    } catch (err: any) { setError(err.message); }
    finally { setTargetUpdating(false); }
  };

  return (
    <TrackingContext.Provider value={{
      activeSession, isOwnSession, activeSegments, isSending, lastPosition, lastUpdateTime, posCount,
      targetUpdating, error, trackingInterval, keepScreenOn,
      handleStartSession, handleStartDakoSession, handleStopSession, handleChangeTarget,
      clearError: () => setError(null), refreshSessions,
    }}>
      {children}
    </TrackingContext.Provider>
  );
};
